import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type {
	CreateInvoiceSequenceArgs,
	GetInvoiceSequenceForCustomerIdResult,
	GetOrCreateInvoiceSequenceDraftResult,
	InvoiceSequenceData,
	InvoiceSequenceHeader,
	UpdateDistributionCentersAssociatedToInvoiceSequenceArgs,
	UpdateInvoiceSequenceDraftArgs,
} from '../ogm-hub';
import * as Hub from '../ogm-hub';

export type IInvoiceSequencesState = EntityState<InvoiceSequenceHeader>;

export const GetInvoiceSequenceId = (
	invoiceSequence: InvoiceSequenceHeader
) => {
	return [invoiceSequence.customerId, invoiceSequence.id].join('|');
};

export const invoiceSequenceAdaptor =
	createEntityAdapter<InvoiceSequenceHeader>({
		selectId: GetInvoiceSequenceId,
	});

export interface getInvoiceSequenceByIdArgs {
	customerId: number;
	sequenceId: number;
}

export const getInvoiceSequenceById = createAsyncThunk<
	InvoiceSequenceHeader,
	getInvoiceSequenceByIdArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/getInvoiceSequenceForCustomer',
	async (
		args: getInvoiceSequenceByIdArgs,
		{ getState, dispatch, rejectWithValue }
	) => {
		const invoiceSequence = selectInvoiceSequenceById(
			getState(),
			args.sequenceId
		);

		if (invoiceSequence == null) {
			const response = await dispatch(
				fetchInvoiceSequencesForCustomer(args.customerId)
			);
			if (fetchInvoiceSequencesForCustomer.fulfilled.match(response)) {
				return response.payload.find((x) => x.id == args.sequenceId);
			} else {
				rejectWithValue(response.payload);
			}
		}

		return invoiceSequence;
	}
);

export const getInvoiceSequenceForCustomer = createAsyncThunk<
	InvoiceSequenceHeader[],
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/getInvoiceSequenceForCustomer',
	async (customerId: number, { getState, dispatch, rejectWithValue }) => {
		const invoiceSequences = selectInvoiceSequenceForCustomer(
			getState(),
			customerId
		);

		if (invoiceSequences.length == 0) {
			const response = await dispatch(
				fetchInvoiceSequencesForCustomer(customerId)
			);
			if (fetchInvoiceSequencesForCustomer.fulfilled.match(response)) {
				return response.payload;
			} else {
				rejectWithValue(response.payload);
			}
		}

		return invoiceSequences;
	}
);

export const fetchInvoiceSequencesForCustomer = createAsyncThunk<
	InvoiceSequenceHeader[],
	number,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/fetchInvoiceSequencesForCustomer',
	async (customerId: number, { rejectWithValue }) => {
		try {
			const response: GetInvoiceSequenceForCustomerIdResult =
				await unwrapFetch2(
					Hub.SequenceController.GetInvoiceSequencesForCustomer(customerId)
				);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			return response.invoiceSequences;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

// get or create draft (will return all invoice categories cmims and products)
// will need to update invoice sequence to be hasDraft if it doesn't already
export const getOrCreateInvoiceSequenceDraft = createAsyncThunk<
	InvoiceSequenceData,
	number,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/getOrCreateInvoiceSequenceDraft',
	async (invoiceSequenceId: number, { dispatch, rejectWithValue }) => {
		try {
			const response: GetOrCreateInvoiceSequenceDraftResult =
				await unwrapFetch2(
					Hub.SequenceController.GetOrCreateInvoiceSequenceDraft(
						invoiceSequenceId
					)
				);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			dispatch(
				InvoiceSequenceSlice.actions.setInvoiceSequenceHasDraft({
					invoiceSequenceId: invoiceSequenceId,
					hasDraft: false,
				})
			);
			return response.draft;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface submitInvoiceSequenceDraftArgs {
	invoiceSequenceId: number;
	invoiceDraftId: number;
}

//submit invoice sequence draft
export const submitInvoiceSequenceDraft = createAsyncThunk<
	void,
	submitInvoiceSequenceDraftArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/submitInvoiceSequenceDraft',
	async (
		invoiceSequenceArgs: submitInvoiceSequenceDraftArgs,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const response = await unwrapFetch2(
				Hub.SequenceController.SubmitInvoiceSequenceDraft(
					invoiceSequenceArgs.invoiceSequenceId,
					invoiceSequenceArgs.invoiceDraftId
				)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			dispatch(
				InvoiceSequenceSlice.actions.setInvoiceSequenceHasDraft({
					invoiceSequenceId: invoiceSequenceArgs.invoiceSequenceId,
					hasDraft: false,
				})
			);
			return;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

//submit invoice sequence draft
export const createInvoiceSequence = createAsyncThunk<
	InvoiceSequenceHeader,
	CreateInvoiceSequenceArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/createInvoiceSequence',
	async (args: CreateInvoiceSequenceArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				Hub.SequenceController.CreateInvoiceSequence(args)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			return response.invoiceSequence;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface deleteInvoiceSequenceDraftArgs {
	invoiceSequenceId: number;
	invoiceDraftId: number;
}

//submit invoice sequence draft
export const deleteInvoiceSequenceDraft = createAsyncThunk<
	void,
	deleteInvoiceSequenceDraftArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/deleteInvoiceSequenceDraft',
	async (
		args: deleteInvoiceSequenceDraftArgs,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const response = await unwrapFetch2(
				Hub.SequenceController.DeleteInvoiceSequenceDraft(
					args.invoiceSequenceId,
					args.invoiceDraftId
				)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			dispatch(
				InvoiceSequenceSlice.actions.setInvoiceSequenceHasDraft({
					invoiceSequenceId: args.invoiceSequenceId,
					draftId: null,
				})
			);
			return;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface updateAssociatedDistrubitonCentersArgs {
	invoiceSequenceId: number;
	args: UpdateDistributionCentersAssociatedToInvoiceSequenceArgs;
}

export const updateAssociatedDistrubitonCenters = createAsyncThunk<
	void,
	updateAssociatedDistrubitonCentersArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/updateAssociatedDistrubitonCenters',
	async (args: updateAssociatedDistrubitonCentersArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				Hub.SequenceController.UpdateAssociatedDistrubitonCenters(
					args.invoiceSequenceId,
					args.args
				)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			return;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface updateDraftArgs {
	invoiceSequenceId: number;
	invoiceSequenceDraftId: number;
	args: UpdateInvoiceSequenceDraftArgs;
}

export const updateInvoiceSequenceDraft = createAsyncThunk<
	void,
	updateDraftArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/updateInvoiceSequenceDraft',
	async (args: updateDraftArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				Hub.SequenceController.UpdateInvoiceSequenceDraft(
					args.invoiceSequenceId,
					args.invoiceSequenceDraftId,
					args.args
				)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			return;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface searchForProductArgs {
	supplierProductNumber: string;
	supplierId: number;
}

export interface ProductWithSupplierCategories {
	id: string;
	corpSupplierCode: string;
	supplierProductNumber: string;
	description: string;
	extendedDescription: string;
	supplierCategoryLevel1Code: string | null;
	supplierCategoryLevel2Code: string | null;
	supplierCategoryLevel3Code: string | null;
	supplierCategoryLevel4Code: string | null;
	supplierCategoryLevel1Description: string | null;
	supplierCategoryLevel2Description: string | null;
	supplierCategoryLevel3Description: string | null;
	supplierCategoryLevel4Description: string | null;
}

export const searchForProduct = createAsyncThunk<
	ProductWithSupplierCategories | null,
	searchForProductArgs,
	{ rejectValue: string[] | Response }
>(
	'invoiceSequenceSlice/searchForProduct',
	async (args: searchForProductArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				Hub.ProductController.GetProductsByProductNumbersAnyStatus(
					args.supplierId,
					[args.supplierProductNumber]
				)
			);

			if (response.errors && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}

			return response.results.length > 0 ? response.results[0] : null;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const InvoiceSequenceSlice = createSlice({
	name: 'invoiceSequenceSlice',
	initialState: invoiceSequenceAdaptor.getInitialState(),
	reducers: {
		setInvoiceSequenceHasDraft: (state, action) => {
			const id = state.ids.filter((x) =>
				x.toString().includes(action.payload.invoiceSequenceId)
			)[0];
			invoiceSequenceAdaptor.updateOne(state, {
				id,
				changes: { draftId: action.payload.draftId },
			});
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			fetchInvoiceSequencesForCustomer.fulfilled,
			(state, action) => {
				invoiceSequenceAdaptor.upsertMany(state, action.payload);
			}
		);
		builder.addCase(createInvoiceSequence.fulfilled, (state, action) => {
			invoiceSequenceAdaptor.upsertOne(state, action.payload);
		});
	},
});

function selectInvoiceSequenceForCustomer(
	state: IAppState,
	customerId: number
): InvoiceSequenceHeader[] {
	const ids = state.invoiceSequences.ids.filter(
		(x) => (x as string).split('|')[0] == customerId.toString()
	);
	return ids.map((id) => state.invoiceSequences.entities[id]);
}

export function selectInvoiceSequenceById(
	state: IAppState,
	sequenceId: number | string
): InvoiceSequenceHeader {
	const ids = state.invoiceSequences.ids.filter(
		(x) => (x as string).split('|')[1] == sequenceId.toString()
	);
	if (ids.length > 0) {
		const test = state.invoiceSequences.entities[ids[0]];
		return test;
	}
	return null;
}
